import {Component, DestroyRef} from '@angular/core';
import {CommonModule, KeyValue} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
    faAngleLeft,
    faAngleRight,
    faArrowDown,
    faArrowUp,
    faBan,
    faCircleExclamation,
    faCircleInfo,
    faCircleXmark,
    faFileExcel,
    faFire,
    faMagnifyingGlass,
    faPen,
    faPenToSquare,
    faSquarePlus,
    faTrash,
    faUpload,
    faWarning
} from '@fortawesome/free-solid-svg-icons';
import {ApiService} from '../services/api.service';
import {ToastrService} from 'ngx-toastr';
import {getFileNameFromResponse} from '../../common/utils';
import saveAs from 'file-saver';
import {GenericSearchComponent} from '../shared/search-suggestion/search-suggestion.component';
import {VehicleNumberCopyDirective} from '../shared/vehicle-number-copy/vehicle-number-copy.directive';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../shared/constants/pagination.constants';
import {RoleService} from '../services/role.service';
import {AuthService} from '../services/auth.service';
import {BulkImportResultComponent} from '../shared/bulk-import-result/bulk-import-result.component';
import {handleBulkImportFallbackError} from '../shared/bulk-import-result/bulk-import-result.utils';
import {BulkUploadFlow} from '../shared/bulk-upload-flow/bulk-upload-flow';
import {VEHICLE_DATABASE_BULK_UPLOAD_SHELL} from '../shared/bulk-upload-flow/bulk-upload-shell.configs';
import {BulkUploadShellComponent} from '../shared/bulk-upload-shell/bulk-upload-shell.component';
import {SharedTableToolbarComponent} from '../shared/table-toolbar/shared-table-toolbar.component';
import {
    buildDeletePayload,
    BulkSelectionState,
    clearSelection,
    createEmptySelection,
    hasSelection,
    isKeySelected,
    isPageFullySelected,
    selectAllMatching,
    selectedCount,
    selectVisiblePage,
    SharedBulkSelectionBannerComponent,
    toggleKey,
} from '../shared/bulk-selection';
import {encodeSortColumnParam, toggleColumnSort} from '../shared/utils/table-sort.utils';
import {ReportingDatePipe} from '../shared/datetime/reporting-date.pipe';
import {VtePaginationComponent} from '../shared/vte/vte-pagination/vte-pagination.component';

interface VehicleRecord {
  sNo: number;
  empId: string;
  vehicleNo: string;
  personName: string;
  addedBy: string;
  createdOn: string;
}

@Component({
    selector: 'app-vehicle-database',
    templateUrl: './vehicle-database.component.html',
    styleUrls: ['./vehicle-database.component.css'],
    imports: [ReportingDatePipe,
        CommonModule,
        FormsModule,
        FontAwesomeModule,
        GenericSearchComponent,
        VehicleNumberCopyDirective,
        BulkImportResultComponent,
        BulkUploadShellComponent,
        SharedTableToolbarComponent,
        SharedBulkSelectionBannerComponent,
        VtePaginationComponent,
    ]
})
export class VehicleDatabaseComponent {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  searchText: string  = '';
  recordsPerPage: number = 10;
  currentPage: number = 1;
  vehicleDatabase:any = []; 
  nextPage: number | null = null;
  previousPage: number | null = null;
  limit: number = 10;
  showConfirmationDialog: boolean = false;
  pId: number = 0;
  totalRecords:number = 0
  todayDate: Date = new Date();
    bulkSelection: BulkSelectionState<number> = createEmptySelection();
    showBulkDeleteDialog = false;
    bulkDeletePreview: any = null;
    bulkDeleting = false;
    showGuidelinesModal = false;
    sampleGuidelines: any = {};
    originalOrder = (_a: KeyValue<number, string>, _b: KeyValue<number, string>): number => 0;
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' | '' = '';
    hoveredColumn: string = '';
  
  // New properties for action tracking
  confirmationTitle: string = '';
  confirmationMessage: string = '';
  currentActionType: 'delete' | 'blacklist' | 'hotlist' = 'delete';
  selectedVehicle: any = null;

    readonly bulkUploadShell = VEHICLE_DATABASE_BULK_UPLOAD_SHELL;
    readonly bulkUpload: BulkUploadFlow;

  constructor(public apiService: ApiService,
    library: FaIconLibrary,
    private router: Router,
              private toastr: ToastrService,
              private roleService: RoleService,
              private authService: AuthService,
              private destroyRef: DestroyRef) {
      this.bulkUpload = new BulkUploadFlow(this.toastr, {
          failureFilename: 'vehicle_bulk_upload_failures.csv',
          defaultSuccessMessage: 'Vehicles uploaded successfully.',
          upload: (formData) => this.apiService.bulkUpload(formData),
      }, this.destroyRef);
    this.fetchData(this.currentPage, this.recordsPerPage);
    library.addIcons(faTrash,
                    faFileExcel,
                    faAngleRight,
                    faAngleLeft,
                    faUpload,
                    faSquarePlus,
                    faMagnifyingGlass,
                    faPen,
                    faPenToSquare,
                    faWarning,
                    faCircleInfo,
        faCircleExclamation,
        faCircleXmark,
                    faBan,
        faFire,
        faArrowUp,
        faArrowDown
                  );
  }

    get canBulkDelete(): boolean {
        const userInfo = this.authService.getCurrentUserInfo?.() ?? null;
        const groups = Array.isArray(userInfo?.role) ? userInfo.role : [userInfo?.role].filter(Boolean);
        return this.roleService.isPlatformSuperuser() || this.roleService.isAdmin(groups);
    }

    get visiblePageIds(): number[] {
        return (this.vehicleDatabase || [])
            .map((row: any) => row?.id)
            .filter((id: unknown): id is number => id != null);
    }

    get bulkSelectedCount(): number {
        return selectedCount(this.bulkSelection);
    }

    get hasBulkSelection(): boolean {
        return hasSelection(this.bulkSelection);
    }

    get allVisibleSelected(): boolean {
        return isPageFullySelected(this.bulkSelection, this.visiblePageIds);
    }

    isVehicleSelected(id: number): boolean {
        return isKeySelected(this.bulkSelection, id);
    }

    toggleVehicleSelection(id: number, checked: boolean): void {
        this.bulkSelection = toggleKey(this.bulkSelection, id, checked);
    }

    toggleSelectAll(checked: boolean): void {
        this.bulkSelection = selectVisiblePage(this.bulkSelection, this.visiblePageIds, checked);
    }

    onSelectAllMatching(): void {
        this.bulkSelection = selectAllMatching(this.bulkSelection, this.totalRecords);
    }

    onClearBulkSelection(): void {
        this.bulkSelection = clearSelection();
    }

    private buildBulkDeletePayload(): Record<string, unknown> {
        return buildDeletePayload(this.bulkSelection, {
            idField: 'vehicle_ids',
            filters: {search: this.searchText || ''},
        });
    }

    openBulkDeletePreview(): void {
        if (!this.canBulkDelete || !this.hasBulkSelection) {
            return;
        }
        this.apiService.previewVehicleBulkDelete(this.buildBulkDeletePayload()).subscribe({
            next: (preview) => {
                this.bulkDeletePreview = preview;
                this.showBulkDeleteDialog = true;
            },
            error: (err) => {
                const detail = err?.error?.detail || 'Unable to analyze delete impact.';
                this.toastr.error(detail, 'Error');
            },
        });
    }

    closeBulkDeleteDialog(): void {
        if (this.bulkDeleting) {
            return;
        }
        this.showBulkDeleteDialog = false;
        this.bulkDeletePreview = null;
    }

    confirmBulkDelete(): void {
        if (!this.canBulkDelete || !this.hasBulkSelection || this.bulkDeleting) {
            return;
        }
        this.bulkDeleting = true;
        this.apiService.bulkDeleteVehicles(this.buildBulkDeletePayload()).subscribe({
            next: (result: any) => {
                this.bulkDeleting = false;
                this.showBulkDeleteDialog = false;
                this.bulkDeletePreview = null;
                this.bulkSelection = clearSelection();
                const deleted = result?.deleted_vehicles ?? 0;
                this.toastr.success(`${deleted} vehicle(s) deleted.`, 'Success');
                this.fetchData(this.currentPage, this.recordsPerPage);
            },
            error: (err) => {
                this.bulkDeleting = false;
                const detail = err?.error?.detail || 'Bulk delete failed.';
                this.toastr.error(detail, 'Error');
            },
        });
    }

  addVehicle() {
    this.router.navigate(['/add-vehicle']);
  }

    bulkUploadOpen() {
        this.bulkUpload.open();
  }

    closeBulkUploadDialog() {
        this.bulkUpload.close();
    }

    onBulkFileSelected(file: File) {
        this.bulkUpload.selectFile(file);
    }

    submitBulkUpload(file?: File) {
        this.bulkUpload.submit(file, () => {
            this.fetchData(this.currentPage, this.recordsPerPage);
        });
    }

    openGuidelines() {
        this.apiService.uploadGuidelines().subscribe({
            next: (res: any) => {
                this.sampleGuidelines = res;
                this.showGuidelinesModal = !!this.sampleGuidelines;
            },
            error: (err) => handleBulkImportFallbackError(err, this.toastr),
        });
    }

    closeGuidelinesModal() {
        this.showGuidelinesModal = false;
  }

    downloadTemplate() {
        this.apiService.downloadImportTemplate('vehicle').subscribe({
            next: (response) => {
                const fileName = getFileNameFromResponse(response);
                if (response.body) {
                    saveAs(response.body, fileName);
                }
            },
            error: (err) => {
                if (err?.error?.detail) {
                    this.toastr.error(err.error.detail, 'Error');
                } else if (err?.error?.error) {
                    this.toastr.error(err.error.error, 'Error');
                }
            },
        });
    }


  fetchData(page: number, pageSize: number){
      const ordering = this.sortColumn
          ? encodeSortColumnParam({
              sortColumn: this.sortColumn,
              sortDirection: this.sortDirection as 'asc' | 'desc' | '',
          })
          : null;
      this.apiService.getVehicleDatabase(page, pageSize, this.searchText, ordering).subscribe(data => {
      this.vehicleDatabase = data['results'];
      this.totalRecords = data['count'];
      this.nextPage = data['next']
      this.previousPage = data['previous']
    });
  }

    sortData(column: string) {
        const next = toggleColumnSort(
            {sortColumn: this.sortColumn, sortDirection: this.sortDirection as 'asc' | 'desc' | ''},
            column,
        );
        this.sortColumn = next.sortColumn;
        this.sortDirection = next.sortDirection;
        this.currentPage = 1;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

  get paginatedRecords(): VehicleRecord[] {
    return []
  }
  get totalPages(): number {
      let totalPage = Math.ceil(this.totalRecords / this.recordsPerPage);
      if (totalPage === 0){
        totalPage = 1;
      }
      return totalPage;
    }

  deleteRecord(index: number) {}
  next() {
    if (this.nextPage){
      this.currentPage++;
      this.fetchData(this.currentPage, this.recordsPerPage);
    }
  }

  previous() {
    if (this.previousPage){
      this.currentPage--;
      this.fetchData(this.currentPage, this.recordsPerPage);
      }
  }

  selectPageSize(){
    if(this.recordsPerPage){
      this.currentPage = 1;
      this.fetchData(this.currentPage, this.recordsPerPage);
    }
  }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    onPageSizeChange(pageSize: number): void {
        this.recordsPerPage = pageSize;
        this.currentPage = 1;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }
  // Model for confirming deletion
  openConfirmationDialog(index: number) {
    this.currentActionType = 'delete';
    this.pId = index;
    this.confirmationTitle = 'Are You Sure?';
    this.confirmationMessage = 'Do you want to permanently delete this vehicle? Confirm your choice below!';
    this.showConfirmationDialog = true;
  }

  openBlacklistConfirmationDialog(vehicle: any) {
    this.currentActionType = 'blacklist';
    this.selectedVehicle = vehicle;
    this.confirmationTitle = 'Blacklist Vehicle?';
    this.confirmationMessage = `Do you want to add vehicle ${vehicle.vehicle_number} to the blacklist? This action will prevent the vehicle from being tracked.`;
    this.showConfirmationDialog = true;
  }

  openHotlistConfirmationDialog(vehicle: any) {
    this.currentActionType = 'hotlist';
    this.selectedVehicle = vehicle;
      this.confirmationTitle = 'Hotlisted Vehicle?';
      this.confirmationMessage = `Do you want to add vehicle ${vehicle.vehicle_number} to Hotlisted Vehicles? This action will flag the vehicle as urgent to monitor.`;
    this.showConfirmationDialog = true;
  }

  closeConfirmationDialog() {
    this.showConfirmationDialog = false;
  }

  confirmAction() {
    switch (this.currentActionType) {
      case 'delete':
        this.confirmDelete();
        break;
      case 'blacklist':
        this.confirmBlacklist();
        break;
      case 'hotlist':
        this.confirmHotlist();
        break;
    }
  }

  private confirmDelete() {
    this.apiService.deleteVehicle(this.pId).subscribe({
      next: (response:any) => {
        this.toastr.success('Vehicle deleted successfully.', 'Success');
        this.closeConfirmationDialog();
        // this.router.navigate(['/vehicle']);
        window.location.reload();
      },
      error: (err) => {
        if (err['error']['detail']) {
            this.toastr.error(err['error']['detail'], 'Error');
          
        }  else if (err['status'] !== 403 && err.status !== 0) {
            this.toastr.error("Something went wrong!", 'Error');
        }
        this.closeConfirmationDialog();
      }
    })
  }

  private confirmBlacklist() {
    const formdata = new FormData();
    formdata.append('vehicle_number', this.selectedVehicle.vehicle_number);
    if (this.selectedVehicle.person_name) {
      formdata.append('person_name', this.selectedVehicle.person_name);
    }
    formdata.append('reason', 'Added from vehicle database');
    formdata.append('is_active', 'true');

    this.apiService.addBlackListedVehicle(formdata).subscribe({
      next: (response:any) => {
        this.toastr.success(`Vehicle ${this.selectedVehicle.vehicle_number} has been blacklisted successfully.`, 'Success');
        this.closeConfirmationDialog();
        window.location.reload();
      },
      error: (err) => {
        if (err['error']['non_field_errors']) {
          err['error']['non_field_errors'].forEach((element:any) => {
            this.toastr.error(element, 'Error');
          });
        } else if (err['error']['vehicle_number']) {
          err['error']['vehicle_number'].forEach((element:any) => {
            this.toastr.error(element, 'Error');
          });
        } else if (err['status'] !== 403 && err.status !== 0) {
          this.toastr.error("Something went wrong!", 'Error');
        }
        this.closeConfirmationDialog();
      }
    });
  }

  private confirmHotlist() {
    const formdata = new FormData();
    formdata.append('vehicle_number', this.selectedVehicle.vehicle_number);
    if (this.selectedVehicle.person_name) {
      formdata.append('person_name', this.selectedVehicle.person_name);
    }
    formdata.append('reason', 'Added from vehicle database');
    formdata.append('is_active', 'true');

    this.apiService.addHotList(formdata).subscribe({
      next: (response:any) => {
          this.toastr.success(`Vehicle ${this.selectedVehicle.vehicle_number} has been added to Hotlisted Vehicles successfully.`, 'Success');
        this.closeConfirmationDialog();
        window.location.reload();
      },
      error: (err) => {
        if (err['error']['non_field_errors']) {
          err['error']['non_field_errors'].forEach((element:any) => {
            this.toastr.error(element, 'Error');
          });
        } else if (err['error']['vehicle_number']) {
          err['error']['vehicle_number'].forEach((element:any) => {
            this.toastr.error(element, 'Error');
          });
        } else if (err['status'] !== 403 && err.status !== 0) {
          this.toastr.error("Something went wrong!", 'Error');
        }
        this.closeConfirmationDialog();
      }
    });
  }

  updateVehicle(pk: number){
    return this.router.navigate([`edit-vehicle/${pk}`])
  }
  
  vehicleSearch(){
    this.currentPage = 1;
      this.bulkSelection = clearSelection();
    this.fetchData(this.currentPage, this.recordsPerPage);
  }

  exportToExcel() {
    this.apiService.exportVehicleDB(this.currentPage, this.recordsPerPage, this.searchText).subscribe({
      next: (response: any) => {
        const fileName = getFileNameFromResponse(response);
          if (response.body) {
              saveAs(response.body, fileName);
          }
       
      },
      error: (err) => {
        this.toastr.error(`${err}`, 'Error')
      }
    })
  }

onResults(event: { searchText: string, results: any[], totalRecords: number, nextPage: number | null, previousPage: number | null }) {
  this.currentPage = 1;
  this.recordsPerPage = 10;
  this.previousPage = event.previousPage;
  this.nextPage = event.nextPage;
  this.searchText = event.searchText;
  this.vehicleDatabase = event.results;
  this.totalRecords = event.totalRecords;
    this.bulkSelection = clearSelection();
}


}
